import { integrationTesting } from '@ps/web-core-utils';
import { times } from 'lodash';

import type { CasinoGameTileProps, CasinoIcon, MosaicLayout } from '@swf/casino';
import { select } from '@swf/core/store';
import type { RootState } from '@swf/core/store';
import { IconTypes, MosaicContext, promoTypes } from '@swf/core/types/CasinoGame';
import type { GameLike, Promo, PromoStream, StreamData } from '@swf/core/types/CasinoGame';

export const UPDATE_THROTTLE_TIME = integrationTesting ? 10 : 5000;

export const isPromo = (test: GameLike | Promo): test is Promo =>
  !!(test as Promo).type && promoTypes.includes((test as Promo).type);

export const injectPromo = <T extends StreamData<GameLike>>(
  stream: T,
  streamIndex: number,
  locale: string,
  renderIcon: (gameIndex: number) => CasinoIcon | undefined,
  promos: PromoStream
): T => {
  if (promos?.version && promos.data[streamIndex]?.length) {
    const toInject = promos.i18n?.[locale]?.[streamIndex] || promos.data[streamIndex];
    const games = stream.games.concat([]);
    toInject
      .sort((a, b) => a.index - b.index)
      .forEach((promo) => {
        const positionIcon = renderIcon(promo.index);
        if (
          positionIcon &&
          (promo.images[positionIcon] || promo.type === 'promo-frame') &&
          promo.index <= games.length &&
          isPromo(promo)
        ) {
          games.splice(promo.index, 0, promo);
        }
      });
    return { ...stream, games };
  }

  return stream;
};

interface ExtractResponse {
  locale: string;
  promotionStream: PromoStream;
}

export const extractPromoStream = (
  config: Partial<{ promotion_stream?: PromoStream; promotion_stream_string: string }>,
  rootState: RootState
): ExtractResponse => {
  const locale = select.locale.get(rootState);
  const {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    promotion_stream = config.promotion_stream ||
      JSON.parse(config.promotion_stream_string || '{"version": 1, "data": []}')
  } = config;
  return { locale, promotionStream: promotion_stream };
};

export const getMosaicProps = (
  mosaic?: MosaicLayout,
  styles: { [id: string]: string } = {},
  gamedeckMosaic?: boolean,
  isNewSLView?: boolean
) => {
  if (gamedeckMosaic) return [{ mosaicHint: MosaicContext.SmallIcon }];
  if (!mosaic?.enabled) return [];
  const icon = isNewSLView ? IconTypes.Poster : IconTypes.Square;

  return ['section_1', 'section_2', 'section_3'].reduce<
    Pick<CasinoGameTileProps, 'noIconScale' | 'icon' | 'className' | 'mosaicHint'>[]
  >((memo, current) => {
    switch (mosaic[current as keyof typeof mosaic]) {
      case '1 big square':
        memo.push({
          noIconScale: true,
          icon,
          className: styles.bigIcon,
          mosaicHint: MosaicContext.BigIcon
        });
        break;
      case '4 squares':
        times(4, () => memo.push({ icon }));
        break;
      case '1 top 2 bottom':
        memo.push({ icon: IconTypes.Wide, className: styles.wide });
        times(2, () => memo.push({ icon }));
        break;
      case '2 top 1 bottom':
        memo.push({ icon });
        memo.push({ icon: IconTypes.Wide, className: styles.wide });
        memo.push({ icon });
        break;
      case '1 top 1 bottom':
        times(2, () => memo.push({ icon: IconTypes.Wide, className: styles.wide }));
        break;
    }
    return memo;
  }, []);
};
